import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box,
  Card,
  Grid,
  Tab,
  Tabs,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  GroupOutlined,
  DashboardOutlined,
  BadgeOutlined,
  RefreshOutlined
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useUser } from 'hooks/useUser';
import { useAdminContext } from '../../../../context/AdminContext';
import ClientSelector from '../../../../components/common/admin/ClientSelector';
import { PeopleAdminProvider, usePeopleAdmin } from './context/PeopleAdminContext';

// Tab panels
import OverviewTab from './tabs/OverviewTab';
import EmployeesTab from './tabs/EmployeesTab';
import RoleDefinitionsTab from './tabs/RoleDefinitionsTab';

/**
 * Query client instance with optimized defaults for PeopleAdmin
 * @type {QueryClient}
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,     // Consider data stale after 5 minutes
      gcTime: 30 * 60 * 1000,        // Keep in cache for 30 minutes
      retry: 1,                       // Only retry once on failure
      refetchOnWindowFocus: false,   // Don't refetch on window focus
      refetchOnMount: false,          // Don't refetch on component mount if data exists
    },
  },
});

/**
 * Tab panel component for displaying content based on selected tab
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display in the panel
 * @param {number} props.value - Current tab value
 * @param {number} props.index - Panel index
 * @returns {React.Component} Tab panel wrapper
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`people-admin-tabpanel-${index}`}
      aria-labelledby={`people-admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * Inner component that renders the PeopleAdmin interface
 * @returns {React.Component} PeopleAdmin content with tabs
 */
function PeopleAdminContent() {
  const [tabValue, setTabValue] = useState(0);
  const [employeeFilters, setEmployeeFilters] = useState({});
  const { user } = useUser();
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin,
    availableClients,
    loading: adminLoading,
    error: adminError
  } = useAdminContext();
  const {
    isLoading,
    isRefetching,
    error,
    refetch
  } = usePeopleAdmin();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear employee filters when switching tabs
    if (newValue !== 1) {
      setEmployeeFilters({});
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const tabs = [
    { label: 'Overview', icon: <DashboardOutlined /> },
    { label: 'Employees', icon: <GroupOutlined /> },
    { label: 'Role Definitions', icon: <BadgeOutlined /> }
  ];

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3}>
          <Card>
            <Box p={3}>
              <Typography color="error">
                Error loading People Admin data: {error.message}
              </Typography>
              <AdminButton onClick={handleRefresh} sx={{ mt: 2 }}>
                Retry
              </AdminButton>
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box p={3}>
                <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h3" fontWeight="bold">
                      People Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.organizationName || 'Organization'}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2} alignItems="center">
                    {isAppAdmin && (
                      <ClientSelector
                        value={selectedClient}
                        onChange={setSelectedClient}
                        clients={availableClients}
                        loading={adminLoading}
                        error={adminError}
                      />
                    )}
                    <AdminButton
                      variant="outlined"
                      startIcon={isRefetching ? <CircularProgress size={16} /> : <RefreshOutlined />}
                      onClick={handleRefresh}
                      disabled={isRefetching}
                    >
                      {isRefetching ? 'Refreshing...' : 'Refresh Data'}
                    </AdminButton>
                  </Box>
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        minHeight: 56,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '& .MuiSvgIcon-root': {
                          color: '#7C3AED',
                          fontSize: '1.5rem'
                        }
                      },
                      '& .Mui-selected': {
                        backgroundColor: 'rgba(124, 58, 237, 0.08)',
                        '& .MuiSvgIcon-root': {
                          color: '#7C3AED'
                        }
                      }
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab
                        key={index}
                        icon={tab.icon}
                        label={tab.label}
                        iconPosition="start"
                        id={`people-admin-tab-${index}`}
                        aria-controls={`people-admin-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TabPanel value={tabValue} index={0}>
                      <OverviewTab 
                        onSwitchToEmployees={(filters) => {
                          if (filters) {
                            setEmployeeFilters(filters);
                          }
                          setTabValue(1);
                        }}
                        onSwitchToRoles={() => setTabValue(2)}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                      <EmployeesTab initialFilters={employeeFilters} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                      <RoleDefinitionsTab />
                    </TabPanel>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}

/**
 * Main PeopleAdmin component with providers
 * @returns {React.Component} Complete PeopleAdmin interface with context providers
 */
function PeopleAdmin() {
  return (
    <QueryClientProvider client={queryClient}>
      <PeopleAdminProvider>
        <PeopleAdminContent />
      </PeopleAdminProvider>
    </QueryClientProvider>
  );
}

export default PeopleAdmin;